// OurApproach.js
import React from 'react';
import { motion } from 'framer-motion';
import { MessageCircle, Eye, Users, Headphones } from 'lucide-react';

const OurApproach = () => {
  const principles = [
    {
      icon: MessageCircle,
      title: 'Consultation First',
      description: 'Every journey begins with an honest consultation. We assess eligibility, clarify expectations, and outline realistic pathways before any commitment is made.',
      color: 'blue'
    },
    {
      icon: Eye,
      title: 'Transparency',
      description: 'We operate with clear communication and defined processes. Candidates understand what we do, what we do not do, and what to expect at every stage.',
      color: 'green'
    },
    {
      icon: Users,
      title: 'Direct Communication',
      description: 'Once institutional or employer contact is established, communication is directly between the candidate and the relevant organisation. We remain available to guide and support throughout.',
      color: 'orange'
    },
    {
      icon: Headphones,
      title: 'Ongoing Support',
      description: 'Our role does not end with an offer or interview. We continue to provide guidance to help candidates understand next steps and prepare appropriately.',
      color: 'purple'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: 'border-t-blue-500',
      green: 'border-t-green-500',
      orange: 'border-t-orange-500',
      purple: 'border-t-purple-500'
    };
    return colors[color] || 'border-t-blue-500';
  };

  return (
    <section className="py-16 bg-[#F5F8FA]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-[#202356] mb-4">
            Our Approach
          </h2>
          <p className="text-gray-600 text-sm font-light max-w-2xl mx-auto">
            Our work is guided by a set of principles that shape every client interaction
          </p>
        </motion.div>

        {/* Principles Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {principles.map((principle, index) => {
            const Icon = principle.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`bg-white p-6 border-t-2 ${getColorClasses(principle.color)} hover:shadow-lg transition-shadow`}
              >
                <Icon size={32} className="text-gray-700 mb-4" />
                
                <h3 className="text-lg font-semibold text-[#202356] mb-3 leading-tight">
                  {principle.title}
                </h3>
                
                <p className="text-gray-600 text-sm font-light leading-relaxed">
                  {principle.description}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default OurApproach;
